<template>
  <div v-if="$ability.can('write', 'Service')">
    <div class="p-grid">
      <div class="p-col-12">
        <AppSkeletonCardForm v-if="loading" :rows="3" />
        <Card v-else id="card-service">
          <template v-slot:title>
            Dados do Serviço
            <span v-if="$route.params.id" class="service-id">
              #{{ $route.params.id }}
            </span>
          </template>
          <template v-slot:content>
            <ItemMetadata :item="service"></ItemMetadata>
            <AppForm
              ref="form"
              :rules="rules"
              :values="service"
              v-slot:default="{ formErrors }"
            >
              <div class="p-fluid p-formgrid p-grid">
                <!-- Cliente -->
                <ServiceEditCustomer
                  v-if="isVet"
                  v-model:customer="service.customer"
                  v-model:customerId="service.customerId"
                  :disabled="!!$route.params.id"
                  @update:customerId="handleCustomerSelect($event)"
                ></ServiceEditCustomer>
                <!-- Tipo -->
                <ServiceEditType
                  v-model:typeId="service.typeId"
                  :type="service.type"
                  :customer="service.customer"
                  :error="formErrors.typeId"
                ></ServiceEditType>
                <!-- Fazenda -->
                <ServiceEditFarm
                  v-if="!isVet || service.customerId"
                  v-model:farm="service.farm"
                  v-model:farmId="service.farmId"
                  :customer="service.customer"
                  :error="formErrors.farmId"
                  :disabled="!$ability.can('read', 'Farm')"
                ></ServiceEditFarm>
                <!-- Data inicio e fim -->
                <ServiceEditDate
                  v-model:dateStart="service.dateStart"
                  v-model:dateEnd="service.dateEnd"
                ></ServiceEditDate>
                <!-- Concluído -->
                <ServiceEditStatus
                  v-model:done="service.done"
                ></ServiceEditStatus>
                <!-- Veterinário -->
                <ServiceEditVet
                  v-if="!isVet"
                  v-model:vet="service.vet"
                  v-model:vetId="service.vetId"
                  :disabled="!!$route.params.id"
                ></ServiceEditVet>
                <!-- Valores -->
                <ServiceEditValues
                  :handleBovine="!isVet || !!service.customerId"
                  v-model:bovineValue="service.bovineValue"
                  v-model:totalValue="service.totalValue"
                  v-model:bovineCount="service.bovineCount"
                ></ServiceEditValues>
                <!-- Registro de caixa -->
                <ServiceEditTransaction
                  v-model:createTransaction="service.createTransaction"
                  v-model:transactionType="service.transactionType"
                ></ServiceEditTransaction>
                <!-- Descrição -->
                <div class="p-field p-col-12">
                  <AppFormItem label="Descrição">
                    <Textarea v-model="service.description" rows="2" />
                  </AppFormItem>
                </div>
                <!-- Notificação -->
                <ServiceEditNotification
                  ref="notifications"
                  :errors="errors"
                  v-model:notifications="service.notifications"
                ></ServiceEditNotification>
              </div>
              <div class="p-grid p-jc-between" style="padding-top: 1rem">
                <div class="p-col-fixed actions-secondary">
                  <Button
                    v-if="$route.params.id"
                    label="Excluir"
                    class="p-button-outlined p-button-danger p-mr-2"
                    icon="pi pi-trash"
                    :disabled="!$ability.can('delete', 'Service')"
                    @click="deleteConfirmVisible = true"
                  />
                  <Button
                    label="Adicionar notificação"
                    class="p-button-outlined"
                    @click="$refs.notifications.add()"
                  />
                </div>
                <div class="p-col-fixed actions-primary">
                  <Button
                    label="Cancelar"
                    class="p-button-outlined p-mr-2"
                    @click="
                      $router.push({
                        name: ($route.name || '').includes('customer')
                          ? 'customer-service.list'
                          : 'service.list',
                      })
                    "
                  />
                  <AppButton
                    label="Salvar"
                    @click="saveAndRedirect()"
                    :loading="loadingSave"
                  />
                </div>
              </div>
            </AppForm>
          </template>
        </Card>
      </div>
      <div
        class="p-col-12"
        v-if="(!isVet || service.customerId) && $ability.can('read', 'Bovine')"
      >
        <ServiceBovineCard
          ref="serviceBovines"
          v-model:bovines="service.bovines"
          v-model:done="service.done"
          v-model:bovineCount="service.bovineCount"
          v-model:dateStart="service.dateStart"
          :serviceId="service?.customerService?.id || Number($route.params.id)"
          :farmId="service.farmId"
          :customer="service.customer"
          @save="saveAndRedirect()"
        />
      </div>
      <div class="p-col-12" v-if="$route.params.id">
        <div class="p-grid p-jc-end p-mt-2">
          <router-link
            tag="span"
            :to="{
              name: ($route.name || '').includes('customer')
                ? 'customer-service.list'
                : 'service.list',
            }"
          >
            <Button label="Voltar" class="p-button-outlined p-mr-2" />
          </router-link>
        </div>
      </div>
    </div>
    <ServiceEditExport v-if="$route.params.id"></ServiceEditExport>
    <AppModalDelete
      type="Serviço"
      :visible="deleteConfirmVisible"
      :item="`Serviço ${service.id} - ${service?.type?.path}`"
      @confirm="onDelete()"
      @cancel="deleteConfirmVisible = false"
    ></AppModalDelete>
  </div>
  <AppPermissionAlert v-else />
</template>

<script>
import { mapGetters } from 'vuex';
import { ServiceApi, VetCustomerApi } from '../../services/api';
import { validateEmail } from '../../lib/helpers';
import ServiceBovineCard from './components/ServiceBovineCard.vue';
import ServiceEditValues from './components/ServiceEditValues.vue';
import ServiceEditExport from './ServiceEdit/ServiceEditExport.vue';
import ServiceEditCustomer from './ServiceEdit/ServiceEditCustomer.vue';
import ServiceEditType from './ServiceEdit/ServiceEditType.vue';
import ServiceEditFarm from './ServiceEdit/ServiceEditFarm.vue';
import ServiceEditDate from './ServiceEdit/ServiceEditDate.vue';
import ServiceEditVet from './ServiceEdit/ServiceEditVet.vue';
import ServiceEditTransaction from './ServiceEdit/ServiceEditTransaction.vue';
import ServiceEditNotification from './ServiceEdit/ServiceEditNotification.vue';
import ServiceEditStatus from './ServiceEdit/ServiceEditStatus.vue';
import ItemMetadata from '../../components/ItemMetadata.vue';

export default {
  name: 'ServiceEdit',
  components: {
    ItemMetadata,
    ServiceBovineCard,
    ServiceEditValues,
    ServiceEditExport,
    ServiceEditCustomer,
    ServiceEditType,
    ServiceEditFarm,
    ServiceEditDate,
    ServiceEditVet,
    ServiceEditTransaction,
    ServiceEditNotification,
    ServiceEditStatus,
  },
  data() {
    return {
      rules: {
        typeId: [{ required: true, message: 'Selecione um tipo.' }],
        farmId: {
          validator: (rule, value, callback) => {
            const isRequired = this.service.customerId || !this.isVet;
            if (isRequired && !value) {
              return callback(new Error('Selecione uma fazenda.'));
            }
            return callback();
          },
        },
      },
      errors: {},
      loading: false,
      loadingSave: false,
      service: {
        typeId: null,
        dateStart: this.getCurrentDate(),
        dateEnd: null,
        description: '',
        done: false,
        bovines: [],
        farmId: null,
        farm: null,
        vetId: null,
        vet: null,
        customerId: null,
        createTransaction: true,
        transactionType: 2,
        bovineValue: 0,
        totalValue: 0,
        notifications: [],
      },
      deleteConfirmVisible: false,
    };
  },
  async created() {
    await this.fetchData();
    if (this.$route.params.bovineImport) {
      const bovineImport = JSON.parse(this.$route.params.bovineImport);
      await this.$refs.serviceBovines.onBovineSelect({
        values: bovineImport.filter(
          (bovine) => bovine.id && bovine.farmId === this.service.farmId
        ),
      });
    }
  },
  computed: {
    ...mapGetters('app', ['isVetAccount']),
    isVet() {
      return (
        this.isVetAccount && !(this.$route.name || '').includes('customer')
      );
    },
  },
  methods: {
    async handleCustomerSelect(customerId) {
      try {
        this.loading = true;
        if (customerId) {
          const loginData = await VetCustomerApi.login({
            accountId: customerId,
          });
          this.service.customer.accessToken = loginData.accessToken;
        }
        this.service = {
          ...this.service,
          type: null,
          typeId: null,
          farm: null,
          farmId: null,
        };
      } catch (error) {
        console.log(error);
        let detail = 'Não foi possível carregar os dados do cliente.';
        this.$toast.add({
          severity: 'error',
          summary: 'Desculpe!',
          detail,
          life: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
    getCurrentDate() {
      const date = new Date();
      date.setHours(0, 0, 0, 0);
      return date;
    },
    async fetchData() {
      try {
        this.loading = true;
        if (!this.$route.params.id) {
          return;
        }
        const service = await ServiceApi.fetchById(this.$route.params.id);
        if (service.customerId) {
          const loginData = await VetCustomerApi.login({
            accountId: service.customerId,
          }).catch(() => {});
          service.customer.accessToken = loginData?.accessToken;
        }
        this.service = {
          ...service,
          dateStart: service.dateStart ? new Date(service.dateStart) : null,
          dateEnd: service.dateEnd ? new Date(service.dateEnd) : null,
          done: !!(
            service.bovineCount &&
            service.bovineCount === service.bovineDoneCount
          ),
          farm: (service.customerService || service).farm,
          farmId: (service.customerService || service).farmId,
          type: (service.customerService || service).type,
          typeId: (service.customerService || service).typeId,
        };
      } finally {
        this.loading = false;
      }
    },
    async validate() {
      this.errors = {};
      let valid = true;
      this.service.notifications.filter((notification, index) => {
        if (notification.email && !validateEmail(notification.email)) {
          this.errors['notificationEmail' + index] = {
            message: 'Informe um email válido.',
          };
          valid = false;
        }
      });
      return (await this.$refs.form.validate()) && valid;
    },
    getDataToSave() {
      const notifications = this.service.notifications.filter(
        (notification) => notification.email
      );
      const data = {
        ...this.service,
        bovines: undefined,
        notifications,
      };
      if (this.service.customerId) {
        data.customerService = {
          farmId: this.service.farmId,
          typeId: this.service.typeId,
        };
      }
      return data;
    },
    async save() {
      try {
        if (!(await this.validate())) {
          return;
        }
        this.loadingSave = true;
        if (!this.$route.params.id) {
          const createResponse = await ServiceApi.create(this.getDataToSave());
          this.service.customerService = createResponse.customerService;
          return createResponse;
        }
        return await ServiceApi.update(
          this.$route.params.id,
          this.getDataToSave()
        );
      } catch (error) {
        this.handleError(error);
      } finally {
        this.loadingSave = false;
      }
    },
    async saveAndRedirect() {
      const service = await this.save();
      if (!service) {
        return;
      }
      if (!this.$route.params.id && (!this.isVet || service.customerId)) {
        const routeName = (this.$route.name || '').includes('customer')
          ? 'customer-service.edit'
          : 'service.edit';
        this.$router.push({ name: routeName, params: { id: service.id } });
      } else {
        this.redirectToList();
      }
    },
    redirectToList() {
      const routeName = (this.$route.name || '').includes('customer')
        ? 'customer-service.list'
        : 'service.list';
      this.$router.push({ name: routeName });
    },
    handleError(error) {
      console.log(error);
      let detail = 'Não foi possível salvar o serviço.';
      this.$toast.add({
        severity: 'error',
        summary: 'Desculpe!',
        detail,
        life: 3000,
      });
    },
    async onDelete() {
      try {
        await ServiceApi.delete(this.$route.params.id);
        this.redirectToList();
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Desculpe!',
          detail: 'Não foi possível excluir o serviço.',
          life: 3000,
        });
      } finally {
        this.deleteConfirmVisible = false;
      }
    },
  },
};
</script>

<style scoped>
#card-service >>> .p-card-content {
  padding-bottom: 0%;
}
.service-id {
  font-size: 1.1rem;
  font-weight: 500;
}
</style>